import { useEffect, useState } from "react";

export interface ChangeEvent {
  type: "added" | "updated" | "removed" | "bulk" | "ready" | "ping";
  id?: string;
  source?: string;
  total?: number;
  added?: number;
  updated?: number;
  removed?: number;
  ts: number;
}

/* -------------------------------------------------------------------- *
 * Module-level SSE singleton.
 *
 * Many components subscribe to live updates at once (hero, grids, list
 * pages). Opening one EventSource per hook meant every mount produced
 * its own connection — and on serverless deployments, where
 * /api/_events legitimately doesn't exist, every mount re-ran the full
 * retry ladder and flooded the console with 404s on each navigation.
 *
 * Instead all hooks share a single connection. After MAX_RETRIES
 * consecutive failures the endpoint is marked dead for the rest of the
 * session and we degrade to refresh-on-navigation.
 * -------------------------------------------------------------------- */

interface Subscriber {
  onEvent: (e: ChangeEvent) => void;
  onConnected: (connected: boolean) => void;
}

const MAX_RETRIES = 4;
const subscribers = new Set<Subscriber>();
let es: EventSource | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let retry = 0;
let dead = false;
let isConnected = false;

function setConnected(next: boolean) {
  if (isConnected === next) return;
  isConnected = next;
  for (const sub of subscribers) sub.onConnected(next);
}

function handleChange(e: MessageEvent<string>) {
  try {
    const event = JSON.parse(e.data) as ChangeEvent;
    if (event.type === "ping") return; // skip heartbeats
    for (const sub of subscribers) sub.onEvent(event);
  } catch {
    /* ignore malformed events */
  }
}

function ensureConnection() {
  if (dead || es || subscribers.size === 0) return;
  if (typeof window === "undefined" || typeof EventSource === "undefined") return;

  es = new EventSource("/api/_events");
  es.onopen = () => {
    retry = 0;
    setConnected(true);
  };
  es.addEventListener("change", handleChange);
  es.onerror = () => {
    setConnected(false);
    es?.close();
    es = null;
    if (retry >= MAX_RETRIES) {
      dead = true;
      return;
    }
    const delay = Math.min(30_000, 1000 * 2 ** retry++);
    reconnectTimer = setTimeout(ensureConnection, delay);
  };
}

function teardownIfIdle() {
  if (subscribers.size > 0) return;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  es?.close();
  es = null;
  isConnected = false;
}

/**
 * Subscribes to /api/_events (Server-Sent Events) and exposes a `version`
 * counter that increments whenever the server's PropertiesStore changes.
 *
 * Components can include `version` in their useEffect dependency array to
 * automatically re-fetch listings when something changes upstream — no
 * manual page refresh required.
 */
export function useLiveProperties(options: { onEvent?: (e: ChangeEvent) => void } = {}): {
  version: number;
  connected: boolean;
  lastEvent: ChangeEvent | null;
} {
  const [version, setVersion] = useState(0);
  const [connected, setConnectedState] = useState(isConnected);
  const [lastEvent, setLastEvent] = useState<ChangeEvent | null>(null);

  useEffect(() => {
    const sub: Subscriber = {
      onEvent: (event) => {
        setLastEvent(event);
        if (
          event.type === "added" ||
          event.type === "updated" ||
          event.type === "removed" ||
          event.type === "bulk" ||
          event.type === "ready"
        ) {
          setVersion((v) => v + 1);
        }
        options.onEvent?.(event);
      },
      onConnected: setConnectedState,
    };
    subscribers.add(sub);
    setConnectedState(isConnected);
    ensureConnection();

    return () => {
      subscribers.delete(sub);
      teardownIfIdle();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { version, connected, lastEvent };
}
